import { useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FormField } from '@/components/shared/FormField';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { swalConfirm } from '@/lib/swal';
import {
  CLASSIFICATIONS,
  CLASSIFICATION_HINTS,
  CLASSIFICATION_LABELS,
  CLOSURE_OUTCOME_HINTS,
  COMMERCIAL_OUTCOME_LABELS,
  OPS_VERDICTS,
  OPS_VERDICT_HINTS,
  OPS_VERDICT_LABELS,
  SCOPE_IMPACTS,
  SCOPE_IMPACT_LABELS,
  opsVerdictRequires,
  triageRequiresQuestion,
} from '@/features/inspection/modification-state-rules';
import { isPdfFile } from '@/features/inspection/quotation-send-rules';
import { useHasPermission } from '@/hooks/use-permissions';
import {
  MODIFICATION_TEXT_MAX,
  inspectionModificationService,
  type ClosureOutcome,
  type CommercialOutcome,
  type ModificationClassification,
  type OpsFeasibilityVerdict,
  type OpsScopeImpact,
} from '@/services/inspection-modification.service';
import type { StaffRequestThread } from '@/services/request-thread.service';
import { useThreadAction } from './useThreadAction';

/**
 * The Request Thread action panels.
 *
 * ── ONE QUESTION DECIDES EVERYTHING HERE ────────────────────────────────────
 * "What capabilities did the server give me?" Nothing in this file reads a role
 * name, a department, a permission hook, `assignedUserId`, `currentOwner`,
 * `waitingFor` or the workflow state to decide whether an action exists. The
 * server already resolved permission ∧ state into `capabilities` by probing the
 * state machine itself; re-deriving any of it here would create a second, weaker
 * rule that could disagree with the API that actually enforces it.
 *
 * That is why the panels below are a flat list of `capabilities.x && <Form/>`.
 * It looks almost too simple. That IS the design.
 *
 * ── AND A CAPABILITY IS STILL ONLY A UX GATE ────────────────────────────────
 * `PermissionGuard` answers 403 and the state machine answers 409 whatever
 * renders. Every form handles a refusal in words and re-reads the thread.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 * Any customer decision control. `canRecordCustomerDecision` does not exist in
 * the Stage C capability set: the staff proxy is inspection-keyed, gated on the
 * legacy `commercialReviewStatus`, and needs a Work Order. It stays on the
 * inspection page where that flow already lives (PW-3).
 *
 * Also absent: any decision about whether an entry is CUSTOMER-visible or
 * INTERNAL. The state machine settled that in C-0 — T5 internal, T6 customer,
 * T13 internal — and it is not re-stated, mirrored or overridden here.
 */

const SELECT_CLASS =
  'flex h-10 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

function ActionCard({
  title,
  description,
  tone = 'neutral',
  children,
  testId,
}: {
  title: string;
  description?: string;
  tone?: 'neutral' | 'primary';
  children: ReactNode;
  testId: string;
}) {
  return (
    <section
      aria-label={title}
      data-testid={testId}
      className={`rounded-lg border px-4 py-4 ${
        tone === 'primary' ? 'border-amber-500/30 bg-amber-500/5' : 'border-border'
      }`}
    >
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function SubmitButton({
  busy,
  label,
  busyLabel,
  disabled,
}: {
  busy: boolean;
  label: string;
  busyLabel: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex justify-end">
      {/* `disabled` while pending is the first guard against a duplicate submit;
          `useThreadAction` holds the second, because a double-click can land
          before React re-renders. */}
      <Button type="submit" size="sm" disabled={busy || disabled}>
        {busy && <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />}
        {busy ? busyLabel : label}
      </Button>
    </div>
  );
}

/** A radio group of choices with a one-line hint each — the pattern the existing
 * verdict and triage forms already use, so a choice never needs training. */
function ChoiceGroup<T extends string>({
  id,
  legend,
  options,
  labels,
  hints,
  value,
  onChange,
  error,
}: {
  id: string;
  legend: string;
  options: readonly T[];
  labels: Record<string, string>;
  hints?: Record<string, string>;
  value: T | null;
  onChange: (next: T) => void;
  error?: string;
}) {
  return (
    <div>
      <p id={`${id}-label`} className="mb-2 text-sm font-medium text-foreground">
        {legend} <span className="text-destructive">*</span>
      </p>
      <div
        role="radiogroup"
        aria-labelledby={`${id}-label`}
        aria-required="true"
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {options.map((option) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              data-option={option}
              onClick={() => onChange(option)}
              className={`min-h-[44px] rounded-lg border px-3 py-2 text-left transition-colors ${
                selected
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-card text-foreground hover:bg-muted'
              }`}
            >
              <span className="block text-sm font-medium">{labels[option] ?? option}</span>
              {hints?.[option] && (
                <span className="block text-xs text-muted-foreground">{hints[option]}</span>
              )}
            </button>
          );
        })}
      </div>
      {error && (
        <p role="alert" className="mt-1 text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

interface PanelProps {
  thread: StaffRequestThread;
}

/** Both ids every mutation needs, straight off the composed read — no lookup. */
function ids(thread: StaffRequestThread) {
  return { inspectionId: thread.inspection.id, roundId: thread.roundId };
}

// ── T2 / T3 / T4 — classify ─────────────────────────────────────────────────

function ClassifyPanel({ thread }: PanelProps) {
  const { inspectionId, roundId } = ids(thread);
  const [classification, setClassification] = useState<ModificationClassification | null>(null);
  const [question, setQuestion] = useState('');
  const [errors, setErrors] = useState<{ classification?: string; question?: string }>({});

  const action = useThreadAction<{
    classification: ModificationClassification;
    question?: string;
  }>({
    roundId,
    run: (vars) => inspectionModificationService.triage(inspectionId, roundId, vars),
    successMessage: (vars) =>
      vars.classification === 'UNCLEAR'
        ? 'Question sent to the customer'
        : vars.classification === 'COMMERCIAL'
          ? 'Classified — ready for your decision'
          : 'Referred to Operations for a feasibility check',
    failureMessage: 'Failed to classify the request',
    onSuccess: () => {
      setClassification(null);
      setQuestion('');
      setErrors({});
    },
  });

  // Mirrors the server's own T4 rule, which it re-validates. Not an
  // authorization decision — a required-field decision.
  const needsQuestion = classification ? triageRequiresQuestion(classification) : false;

  const onSubmit = async () => {
    const next: typeof errors = {};
    if (!classification) next.classification = 'Choose how to route this request.';
    if (needsQuestion && !question.trim()) next.question = 'Write the question for the customer.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !classification) return;

    const ok = await swalConfirm({
      title:
        classification === 'UNCLEAR'
          ? 'Ask the customer to clarify?'
          : `Classify as ${CLASSIFICATION_LABELS[classification]}?`,
      text: CLASSIFICATION_HINTS[classification],
      confirmText: 'Confirm',
    });
    if (!ok) return;
    action.submit({ classification, ...(needsQuestion ? { question: question.trim() } : {}) });
  };

  return (
    <ActionCard
      title="Classify the request"
      description="Does this need an Operations feasibility check, or can Commercial answer it directly?"
      tone="primary"
      testId="action-classify"
    >
      <form
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <fieldset disabled={action.busy} className="space-y-4">
          <ChoiceGroup
            id="thread-classification"
            legend="Classification"
            options={CLASSIFICATIONS}
            labels={CLASSIFICATION_LABELS}
            hints={CLASSIFICATION_HINTS}
            value={classification}
            onChange={(next) => {
              setClassification(next);
              setErrors({});
            }}
            error={errors.classification}
          />
          {needsQuestion && (
            <FormField
              label="Question for the customer"
              htmlFor="thread-triage-question"
              required
              error={errors.question}
            >
              <Textarea
                id="thread-triage-question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={3}
                maxLength={MODIFICATION_TEXT_MAX}
                aria-required="true"
                placeholder="What do you need the customer to explain?"
              />
            </FormField>
          )}
          <SubmitButton busy={action.busy} label="Confirm" busyLabel="Saving…" />
        </fieldset>
      </form>
    </ActionCard>
  );
}

// ── T5 / T6 — the Operations feasibility verdict ────────────────────────────

interface VerdictForm {
  verdict: OpsFeasibilityVerdict | null;
  technicalNote: string;
  alternativeDescription: string;
  clarificationQuestion: string;
  scopeImpact: OpsScopeImpact | '';
  affectsParts: boolean;
  affectsLabour: boolean;
}

const EMPTY_VERDICT: VerdictForm = {
  verdict: null,
  technicalNote: '',
  alternativeDescription: '',
  clarificationQuestion: '',
  scopeImpact: '',
  affectsParts: false,
  affectsLabour: false,
};

function OpsVerdictPanel({ thread }: PanelProps) {
  const { inspectionId, roundId } = ids(thread);
  const [form, setForm] = useState<VerdictForm>(EMPTY_VERDICT);
  const [errors, setErrors] = useState<Partial<Record<keyof VerdictForm, string>>>({});

  const set = <K extends keyof VerdictForm>(key: K, value: VerdictForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const action = useThreadAction<{ verdict: OpsFeasibilityVerdict; form: VerdictForm }>({
    roundId,
    run: ({ verdict, form: f }) =>
      inspectionModificationService.recordOpsVerdict(inspectionId, roundId, {
        verdict,
        technicalNote: f.technicalNote.trim(),
        ...(f.alternativeDescription.trim()
          ? { alternativeDescription: f.alternativeDescription.trim() }
          : {}),
        ...(f.clarificationQuestion.trim()
          ? { clarificationQuestion: f.clarificationQuestion.trim() }
          : {}),
        ...(f.scopeImpact ? { scopeImpact: f.scopeImpact } : {}),
        affectsParts: f.affectsParts,
        affectsLabour: f.affectsLabour,
      }),
    successMessage: ({ verdict }) =>
      verdict === 'MORE_INFORMATION_REQUIRED'
        ? 'Question sent to Commercial to relay to the customer'
        : 'Feasibility response sent to Commercial',
    failureMessage: 'Failed to send the feasibility response',
    onSuccess: () => {
      setForm(EMPTY_VERDICT);
      setErrors({});
    },
  });

  // The server's own `opsVerdictRequires`, imported rather than restated, and
  // re-validated server-side. It decides which FIELDS a verdict needs — not who
  // may answer.
  const needs = form.verdict ? opsVerdictRequires(form.verdict) : null;

  const onSubmit = async () => {
    const next: typeof errors = {};
    if (!form.verdict) next.verdict = 'Choose a feasibility response.';
    if (!form.technicalNote.trim()) next.technicalNote = 'Explain what was checked, and why.';
    if (needs?.alternativeDescription && !form.alternativeDescription.trim())
      next.alternativeDescription = 'Describe the alternative that would work.';
    // T6 — the customer is about to be asked something. Without this there is
    // nothing for them to answer.
    if (needs?.clarificationQuestion && !form.clarificationQuestion.trim())
      next.clarificationQuestion = 'Write the question for the customer.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !form.verdict) return;

    const verdict = form.verdict;
    const ok = await swalConfirm({
      title:
        verdict === 'MORE_INFORMATION_REQUIRED'
          ? 'Ask the customer for more information?'
          : 'Send feasibility response to Commercial?',
      text:
        verdict === 'MORE_INFORMATION_REQUIRED'
          ? 'Commercial will relay your question to the customer. The request returns to Operations when they answer.'
          : `Your response — ${OPS_VERDICT_LABELS[verdict]} — goes to Commercial, who will respond to the customer.`,
      confirmText: 'Send',
    });
    if (!ok) return;
    action.submit({ verdict, form });
  };

  return (
    <ActionCard
      title="Operations review — feasibility"
      description="Is the requested change technically possible, and what would it change about the work?"
      tone="primary"
      testId="action-ops-verdict"
    >
      <form
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <fieldset disabled={action.busy} className="space-y-4">
          <ChoiceGroup
            id="thread-verdict"
            legend="Feasibility"
            options={OPS_VERDICTS}
            labels={OPS_VERDICT_LABELS}
            hints={OPS_VERDICT_HINTS}
            value={form.verdict}
            onChange={(next) => set('verdict', next)}
            error={errors.verdict}
          />

          <FormField
            label="Technical explanation"
            htmlFor="thread-technical-note"
            required
            error={errors.technicalNote}
          >
            <Textarea
              id="thread-technical-note"
              value={form.technicalNote}
              onChange={(e) => set('technicalNote', e.target.value)}
              rows={4}
              maxLength={MODIFICATION_TEXT_MAX}
              aria-required="true"
              placeholder="What was checked, and why the change is or is not possible…"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Internal. The customer sees this only if Commercial chooses to share it.
            </p>
          </FormField>

          {needs?.alternativeDescription && (
            <FormField
              label="Alternative that would work"
              htmlFor="thread-alternative"
              required
              error={errors.alternativeDescription}
            >
              <Textarea
                id="thread-alternative"
                value={form.alternativeDescription}
                onChange={(e) => set('alternativeDescription', e.target.value)}
                rows={3}
                maxLength={MODIFICATION_TEXT_MAX}
                aria-required="true"
                placeholder="Describe the alternative approach…"
              />
            </FormField>
          )}

          {needs?.clarificationQuestion && (
            <FormField
              label="Question for the customer"
              htmlFor="thread-clarification-question"
              required
              error={errors.clarificationQuestion}
            >
              <Textarea
                id="thread-clarification-question"
                value={form.clarificationQuestion}
                onChange={(e) => set('clarificationQuestion', e.target.value)}
                rows={3}
                maxLength={MODIFICATION_TEXT_MAX}
                aria-required="true"
                placeholder="What do you need to know before you can answer?"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Commercial relays this to the customer; Operations never messages the customer
                directly.
              </p>
            </FormField>
          )}

          {form.verdict && !needs?.clarificationQuestion && (
            <div className="space-y-3 rounded-lg border border-border px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Impact on the work (no pricing)
              </p>
              <div>
                <label
                  htmlFor="thread-scope-impact"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Scope of work
                </label>
                <select
                  id="thread-scope-impact"
                  value={form.scopeImpact}
                  onChange={(e) => set('scopeImpact', e.target.value as OpsScopeImpact | '')}
                  className={SELECT_CLASS}
                >
                  <option value="">Not stated</option>
                  {SCOPE_IMPACTS.map((s) => (
                    <option key={s} value={s}>
                      {SCOPE_IMPACT_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={form.affectsParts}
                    onChange={(e) => set('affectsParts', e.target.checked)}
                  />
                  Affects parts
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={form.affectsLabour}
                    onChange={(e) => set('affectsLabour', e.target.checked)}
                  />
                  Affects labour
                </label>
              </div>
            </div>
          )}

          <SubmitButton busy={action.busy} label="Send response" busyLabel="Sending…" />
        </fieldset>
      </form>
    </ActionCard>
  );
}

// ── T13 — refer back to Operations ──────────────────────────────────────────

function ReferToOpsPanel({ thread }: PanelProps) {
  const { inspectionId, roundId } = ids(thread);
  const [note, setNote] = useState('');

  const action = useThreadAction<{ note: string }>({
    roundId,
    run: ({ note: n }) =>
      inspectionModificationService.referToOps(inspectionId, roundId, {
        ...(n.trim() ? { note: n.trim() } : {}),
      }),
    successMessage: 'Referred to Operations',
    failureMessage: 'Failed to refer the request to Operations',
    onSuccess: () => setNote(''),
  });

  return (
    <ActionCard
      title="Refer to Operations"
      description="Send the request back for another technical opinion."
      testId="action-refer-to-ops"
    >
      <form
        noValidate
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          action.submit({ note });
        }}
      >
        <fieldset disabled={action.busy} className="space-y-3">
          <FormField label="Note for Operations (optional)" htmlFor="thread-referral-note">
            <Textarea
              id="thread-referral-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={MODIFICATION_TEXT_MAX}
              placeholder="What would you like them to look at again?"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Staff only — this is not shown to the customer.
            </p>
          </FormField>
          <SubmitButton busy={action.busy} label="Refer to Operations" busyLabel="Referring…" />
        </fieldset>
      </form>
    </ActionCard>
  );
}

// ── T12 — ask the customer ──────────────────────────────────────────────────

function ClarificationPanel({ thread }: PanelProps) {
  const { inspectionId, roundId } = ids(thread);
  const [question, setQuestion] = useState('');
  const [error, setError] = useState<string | undefined>();

  const action = useThreadAction<{ question: string }>({
    roundId,
    run: ({ question: q }) =>
      inspectionModificationService.clarify(inspectionId, roundId, { question: q.trim() }),
    successMessage: 'Question sent to the customer',
    failureMessage: 'Failed to send the question',
    onSuccess: () => {
      setQuestion('');
      setError(undefined);
    },
  });

  const onSubmit = async () => {
    if (!question.trim()) {
      setError('Write the question for the customer.');
      return;
    }
    const ok = await swalConfirm({
      title: 'Send this question to the customer?',
      text: 'The request waits for their answer before it can move on.',
      confirmText: 'Send',
    });
    if (!ok) return;
    action.submit({ question });
  };

  return (
    <ActionCard
      title="Ask the customer a question"
      description="The request waits with the customer until they answer."
      testId="action-request-clarification"
    >
      <form
        noValidate
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <fieldset disabled={action.busy} className="space-y-3">
          <FormField label="Question" htmlFor="thread-clarify" required error={error}>
            <Textarea
              id="thread-clarify"
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                setError(undefined);
              }}
              rows={3}
              maxLength={MODIFICATION_TEXT_MAX}
              aria-required="true"
              placeholder="What do you need the customer to confirm?"
            />
            <p className="mt-1 text-xs text-muted-foreground">The customer will read this.</p>
          </FormField>
          <SubmitButton busy={action.busy} label="Send question" busyLabel="Sending…" />
        </fieldset>
      </form>
    </ActionCard>
  );
}

// ── T8 / T9 / T10 — close ───────────────────────────────────────────────────

function ClosePanel({ thread }: PanelProps) {
  const { inspectionId, roundId } = ids(thread);
  // THE SERVER'S LIST. Not a client constant, not `closureOutcomesFor`, not a
  // filter over the enum — whatever `allowedClosureOutcomes` contains is what is
  // offered, so the NOT_FEASIBLE precondition (T10: Operations must have said so
  // on THIS round) is honoured without being restated.
  const allowed = thread.capabilities.allowedClosureOutcomes;
  const [outcome, setOutcome] = useState<CommercialOutcome | null>(null);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ outcome?: string; message?: string }>({});

  const action = useThreadAction<{ outcome: CommercialOutcome; closureMessage: string }>({
    roundId,
    run: ({ outcome: o, closureMessage }) =>
      inspectionModificationService.close(inspectionId, roundId, {
        // Narrowed at the boundary only: the server decided which outcomes are
        // legal and re-validates this one. The client never widens the set.
        outcome: o as ClosureOutcome,
        closureMessage,
      }),
    successMessage: 'Request closed',
    failureMessage: 'Failed to close the request',
    onSuccess: () => {
      setOutcome(null);
      setMessage('');
      setErrors({});
    },
  });

  const onSubmit = async () => {
    const next: typeof errors = {};
    if (!outcome) next.outcome = 'Choose how this request is being closed.';
    if (!message.trim()) next.message = 'Write the answer the customer will read.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !outcome) return;

    const ok = await swalConfirm({
      title: `Close as “${COMMERCIAL_OUTCOME_LABELS[outcome]}”?`,
      text: 'The customer will see your message. A further request from them opens a new request.',
      confirmText: 'Close request',
    });
    if (!ok) return;
    action.submit({ outcome, closureMessage: message.trim() });
  };

  return (
    <ActionCard
      title="Close the request"
      description="Give the customer an answer and end this request."
      tone="primary"
      testId="action-close"
    >
      <form
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <fieldset disabled={action.busy} className="space-y-4">
          <ChoiceGroup
            id="thread-closure"
            legend="Outcome"
            options={allowed}
            labels={COMMERCIAL_OUTCOME_LABELS}
            hints={CLOSURE_OUTCOME_HINTS as Record<string, string>}
            value={outcome}
            onChange={(next) => {
              setOutcome(next);
              setErrors({});
            }}
            error={errors.outcome}
          />
          <FormField
            label="Message to the customer"
            htmlFor="thread-closure-message"
            required
            error={errors.message}
          >
            <Textarea
              id="thread-closure-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={MODIFICATION_TEXT_MAX}
              aria-required="true"
              placeholder="What are you telling the customer?"
            />
          </FormField>
          <SubmitButton busy={action.busy} label="Close request" busyLabel="Closing…" />
        </fieldset>
      </form>
    </ActionCard>
  );
}

// ── T11 — send a revised quotation ──────────────────────────────────────────

function SendRevisionPanel({ thread }: PanelProps) {
  const { inspectionId, roundId } = ids(thread);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{ reference?: string; file?: string }>({});

  const action = useThreadAction<{ reference: string; reason: string; file: File }>({
    roundId,
    // The established three-step send, unchanged: create/update the DRAFT,
    // attach the ERP PDF, then send. A DIFFERENT permission from closing
    // (`inspection:commercial:send`), which is why it is its own capability.
    run: async ({ reference: ref, reason: why, file: pdf }) => {
      const draft = await inspectionModificationService.upsertDraftRevision(inspectionId, {
        quotationReference: ref.trim(),
        ...(why.trim() ? { revisionReason: why.trim() } : {}),
        roundId,
      });
      await inspectionModificationService.attachRevisionDocument(inspectionId, draft.id, pdf);
      return inspectionModificationService.sendRevision(inspectionId, draft.id, { roundId });
    },
    successMessage: 'Revised quotation sent to the customer',
    failureMessage: 'Failed to send the revised quotation',
    onSuccess: () => {
      setReference('');
      setReason('');
      setFile(null);
      setErrors({});
    },
  });

  const pickFile = (picked: File | null) => {
    if (!picked) return;
    if (!isPdfFile(picked)) {
      setFile(null);
      setErrors((e) => ({ ...e, file: 'Only PDF files are accepted for the quotation.' }));
      return;
    }
    setFile(picked);
    setErrors((e) => ({ ...e, file: undefined }));
  };

  const onSubmit = async () => {
    const next: typeof errors = {};
    if (!reference.trim())
      next.reference = 'Enter the ERP quotation reference for the revised quotation.';
    if (!file) next.file = 'Attach the quotation PDF issued by the ERP.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !file) return;

    const ok = await swalConfirm({
      title: 'Send the revised quotation?',
      text: `Quotation ${reference.trim()} (${file.name}) becomes the current quotation for the customer to approve or reject. The previous one is kept as superseded.`,
      confirmText: 'Send',
    });
    if (!ok) return;
    action.submit({ reference, reason, file });
  };

  return (
    <ActionCard
      title="Send a revised quotation"
      description="Issue the revised ERP quotation and put it to the customer."
      testId="action-send-revision"
    >
      <form
        noValidate
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <fieldset disabled={action.busy} className="space-y-3">
          <FormField
            label="ERP quotation reference"
            htmlFor="thread-revision-reference"
            required
            error={errors.reference}
          >
            <input
              id="thread-revision-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              aria-required="true"
              className={SELECT_CLASS}
              placeholder="e.g. QT-2026-000123-R2"
            />
          </FormField>
          <FormField label="Reason for the revision (optional)" htmlFor="thread-revision-reason">
            <Textarea
              id="thread-revision-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={MODIFICATION_TEXT_MAX}
            />
          </FormField>
          <FormField
            label="Quotation PDF"
            htmlFor="thread-revision-file"
            required
            error={errors.file}
          >
            <input
              id="thread-revision-file"
              type="file"
              accept="application/pdf"
              aria-required="true"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-muted file:px-3 file:py-2 file:text-sm file:text-foreground"
            />
            {file && <p className="mt-1 text-xs text-muted-foreground">{file.name}</p>}
          </FormField>
          <SubmitButton busy={action.busy} label="Send revised quotation" busyLabel="Sending…" />
        </fieldset>
      </form>
    </ActionCard>
  );
}

// ── Share the Operations note ───────────────────────────────────────────────

function PublishOpsNotePanel({ thread }: PanelProps) {
  const { inspectionId, roundId } = ids(thread);

  const action = useThreadAction<void>({
    roundId,
    run: () => inspectionModificationService.publishOpsNote(inspectionId, roundId),
    successMessage: 'Operations note shared with the customer',
    failureMessage: 'Failed to share the Operations note',
  });

  const onClick = async () => {
    const ok = await swalConfirm({
      title: 'Share the Operations note with the customer?',
      text: 'A copy is added to the request for the customer to read. The original note is unchanged.',
      confirmText: 'Share',
    });
    if (!ok) return;
    action.submit(undefined as void);
  };

  return (
    <ActionCard
      title="Share the Operations note"
      description="Publish Operations’ technical explanation to the customer."
      testId="action-publish-ops-note"
    >
      {thread.opsTechnicalNote && (
        <p className="mb-3 whitespace-pre-wrap break-words rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
          {thread.opsTechnicalNote}
        </p>
      )}
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={action.busy}
          onClick={() => void onClick()}
        >
          {action.busy && (
            <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
          )}
          {action.busy ? 'Sharing…' : 'Share with the customer'}
        </Button>
      </div>
    </ActionCard>
  );
}

// ── T18 — an internal note ──────────────────────────────────────────────────

function InternalNotePanel({ thread }: PanelProps) {
  const { inspectionId, roundId } = ids(thread);
  const [body, setBody] = useState('');
  const can = useHasPermission();

  // ── THE ONE PLACE THIS FILE READS A PERMISSION, AND WHY ───────────────────
  // WHETHER the panel renders is decided entirely by `canAddInternalNote`, above
  // and elsewhere. This decides only WHICH URL to post to, because D-131 gives
  // the identical action two routes:
  //
  //   POST …/note      → inspection:modification:manage   (Commercial)
  //   POST …/ops-note  → inspection:modification:review   (Operations)
  //
  // Both call the same `internalNote` service and append the same INTERNAL entry;
  // only the permission gating them differs. The capability object cannot tell
  // them apart — `canAddInternalNote` is `(manage ∨ review) ∧ open` — and in
  // AWAITING_CUSTOMER_* no state-gated capability is true for either department,
  // so the caller's department is genuinely not derivable from capabilities.
  //
  // Inferring it from the other capabilities would be wrong in exactly that case
  // and would send a Commercial user to the Operations route for a 403. This is
  // an architectural wrinkle in the backend, reported with the C-v findings; the
  // clean fix is one route, not a client-side guess.
  const write = can('inspection:modification:manage')
    ? inspectionModificationService.addCommercialNote
    : inspectionModificationService.addOpsNote;

  const action = useThreadAction<{ body: string }>({
    roundId,
    run: ({ body: b }) => write(inspectionId, roundId, b.trim()),
    successMessage: 'Internal note added',
    failureMessage: 'Failed to add the note',
    onSuccess: () => setBody(''),
  });

  return (
    <ActionCard title="Add an internal note" testId="action-internal-note">
      <form
        noValidate
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!body.trim()) return;
          action.submit({ body });
        }}
      >
        <fieldset disabled={action.busy} className="space-y-3">
          <FormField
            label="Internal note (never shown to the customer)"
            htmlFor="thread-internal-note"
          >
            <Textarea
              id="thread-internal-note"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              maxLength={MODIFICATION_TEXT_MAX}
              placeholder="Add context for your team…"
            />
          </FormField>
          <SubmitButton
            busy={action.busy}
            label="Add note"
            busyLabel="Adding…"
            disabled={!body.trim()}
          />
        </fieldset>
      </form>
    </ActionCard>
  );
}

/**
 * Every action this caller may take, and nothing else.
 *
 * The order follows the workflow: classify, then answer, then decide, then the
 * optional extras. Each line is a capability the SERVER granted — there is no
 * `&&` here on a role, a department, a state or an assignee.
 */
export function ThreadActions({ thread }: PanelProps) {
  const c = thread.capabilities;
  return (
    <div className="space-y-4" data-testid="thread-actions">
      {c.canClassify && <ClassifyPanel thread={thread} />}
      {c.canRecordOpsVerdict && <OpsVerdictPanel thread={thread} />}
      {c.canClose && <ClosePanel thread={thread} />}
      {c.canSendRevision && <SendRevisionPanel thread={thread} />}
      {c.canRequestClarification && <ClarificationPanel thread={thread} />}
      {c.canReferToOps && <ReferToOpsPanel thread={thread} />}
      {c.canPublishOpsNote && <PublishOpsNotePanel thread={thread} />}
      {c.canAddInternalNote && <InternalNotePanel thread={thread} />}
    </div>
  );
}

/** True when the caller has at least one control to render. Presentation only —
 * `actionRequired` (which excludes the two optional actions) still comes from
 * the server and is what the header uses. */
export function hasAnyAction(thread: StaffRequestThread): boolean {
  const c = thread.capabilities;
  return (
    c.canClassify ||
    c.canRecordOpsVerdict ||
    c.canReferToOps ||
    c.canRequestClarification ||
    c.canClose ||
    c.canSendRevision ||
    c.canPublishOpsNote ||
    c.canAddInternalNote
  );
}
